import React, { useState } from 'react';
import { Card, Col, Row, Statistic, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import salesConversionReportsService from '@/services/salesConversionReports.service';
import { defaultSalesReportFilters, SalesReportFilterBar } from './SalesReportFilterBar';

const { Title } = Typography;

const SalesOpportunityConversionPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(defaultSalesReportFilters());
  const { data, isLoading } = useQuery({
    queryKey: ['sales-opp-conversion', filters],
    queryFn: () => salesConversionReportsService.opportunityConversion(filters),
  });

  const pipeline = (data?.open_pipeline as Record<string, unknown>) || {};
  const period = (data?.period_conversion as Record<string, unknown>) || {};
  const stages = (data?.stage_distribution as Array<Record<string, unknown>>) || [];
  const stale = (data?.stale_opportunities as Array<Record<string, unknown>>) || [];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Opportunity conversion</Title>
      <SalesReportFilterBar value={filters} onChange={setFilters} />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card loading={isLoading}>
            <Statistic title="Open pipeline" value={Number(pipeline.count || 0)} />
            <div>{formatCurrency(Number(pipeline.value || 0))}</div>
            <div style={{ color: '#666' }}>Weighted {formatCurrency(Number(pipeline.weighted_value || 0))}</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card loading={isLoading}>
            <Statistic title="Created → Quote" value={period.to_quote_pct == null ? '—' : `${period.to_quote_pct}%`} />
            <div>
              {Number(period.converted_to_quotation || 0)} / {Number(period.created_in_period || 0)}
              {period.to_quote_change_pp != null ? ` (Δ ${period.to_quote_change_pp} pp)` : ''}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card loading={isLoading}>
            <Statistic title="Created → SO" value={period.to_so_pct == null ? '—' : `${period.to_so_pct}%`} />
            <div>
              {Number(period.converted_to_so || 0)} / {Number(period.created_in_period || 0)}
              {period.to_so_change_pp != null ? ` (Δ ${period.to_so_change_pp} pp)` : ''}
            </div>
          </Card>
        </Col>
      </Row>
      <Card title="Open stage distribution" style={{ marginBottom: 16 }}>
        <Table
          loading={isLoading}
          size="small"
          rowKey={(r) => String(r.stage)}
          dataSource={stages}
          pagination={false}
          columns={[
            { title: 'Stage', dataIndex: 'stage' },
            { title: 'Count', dataIndex: 'count', align: 'right' },
            { title: 'Value', dataIndex: 'value', align: 'right', render: (v) => formatCurrency(Number(v || 0)) },
          ]}
        />
      </Card>
      <Card title="Stale open opportunities (30+ days)">
        <Table
          loading={isLoading}
          size="small"
          rowKey={(r) => String(r.id)}
          dataSource={stale}
          onRow={(r) => ({
            onClick: () => navigate(`/growth/opportunities/${String(r.id)}`),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Number', dataIndex: 'opportunity_number' },
            { title: 'Name', dataIndex: 'opportunity_name', ellipsis: true },
            { title: 'Customer', dataIndex: 'customer_name' },
            { title: 'Owner', dataIndex: 'sales_owner' },
            { title: 'Stage', dataIndex: 'stage' },
            {
              title: 'Value',
              dataIndex: 'estimated_value',
              align: 'right',
              render: (v) => formatCurrency(Number(v || 0)),
            },
            {
              title: 'Last activity',
              dataIndex: 'last_activity',
              render: (v) => (v ? dayjs(String(v)).format('DD-MMM-YYYY') : '—'),
            },
          ]}
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default SalesOpportunityConversionPage;
